import type { LogDataType } from "../types/application-logger.types";
import { ApplicationLoggerOutput } from "../types/application-logger-output.types";

// ANSI SGR codes: 31=red, 33=yellow, 90=bright black (gray), 39=reset.
const ANSI_RED = "\x1B[31m";
const ANSI_YELLOW = "\x1B[33m";
const ANSI_GRAY = "\x1B[90m";
const ANSI_RESET = "\x1B[39m";

export interface ConsoleOutputOptions {
  // Defaults to false. Callers (e.g. backend logger) should compute support
  // via TTY/NO_COLOR before opting in — this class makes no such check.
  enableColors?: boolean;
}

export class ConsoleOutputImpl extends ApplicationLoggerOutput {
  private readonly includeTimestamps: boolean = true;

  private readonly enableColors: boolean;

  constructor(options?: ConsoleOutputOptions) {
    super();
    this.enableColors = options?.enableColors ?? false;
  }

  public override error(
    description: string,
    location?: string,
    error?: Error,
    context?: LogDataType[],
  ): void {
    const logParts = [
      this.getTimeStamp(undefined, " "),
      "ERROR: " + description,
    ];
    if (location !== undefined && location !== "") {
      logParts.push("\nat -> " + location);
    }
    if (error !== undefined) {
      logParts.push("\nerror -> " + this.stringifyParam(error));
    }
    if (context !== undefined && context.length > 0) {
      context.forEach((item, index) => {
        logParts.push(`\ncontext[${index}] -> ` + this.stringifyParam(item));
      });
    }
    console.error(this.paint(logParts.join(""), ANSI_RED));
  }

  public override warn(...params: LogDataType[]): void {
    console.warn(
      this.paint(this.formatLogMessage("WARN", params), ANSI_YELLOW),
    );
  }

  public override info(...params: LogDataType[]): void {
    console.info(this.formatLogMessage("INFO", params));
  }

  public override debug(...params: LogDataType[]): void {
    console.debug(
      this.paint(this.formatLogMessage("DEBUG", params), ANSI_GRAY),
    );
  }

  public override log(...params: LogDataType[]): void {
    console.log(this.formatLogMessage("LOG", params));
  }

  private formatLogMessage(level: string, params: LogDataType[]): string {
    const [first, ...rest] = params;
    const header =
      this.getTimeStamp(undefined, " ") +
      level +
      ": " +
      (first === undefined ? "" : this.stringifyParam(first));
    if (rest.length === 0) {
      return header;
    }
    return (
      header +
      "\n" +
      rest
        .map((param, index) => `p${index + 1} -> ${this.stringifyParam(param)}`)
        .join("\n")
    );
  }

  private stringifyParam(param: LogDataType): string {
    if (param instanceof Error) {
      return JSON.stringify(
        {
          name: param.name,
          message: param.message,
          stack: param.stack,
        },
        null,
        2,
      );
    }
    if (typeof param === "object" && param !== null) {
      return JSON.stringify(param);
    }
    return String(param);
  }

  private getTimeStamp(prefix?: string, suffix?: string): string {
    if (this.includeTimestamps) {
      return (prefix ?? "") + new Date().toISOString() + (suffix ?? "");
    }
    return "";
  }

  // Wrap each line independently so per-line log capture (e.g. docker-compose's
  // json-file driver) preserves color on every line, not just the first.
  private paint(message: string, ansiColor: string): string {
    if (!this.enableColors) {
      return message;
    }
    return message
      .split("\n")
      .map((line) => `${ansiColor}${line}${ANSI_RESET}`)
      .join("\n");
  }
}
